import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Communication, Member } from '../types';
import {
  MessageSquare,
  Mail,
  Phone,
  Smartphone,
  PhoneCall,
  Calendar,
  Sparkles,
  Send,
  Clock,
} from 'lucide-react';

export default function OutreachCenter() {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<
    'SMS' | 'Email' | 'WhatsApp' | 'IVR'
  >('SMS');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('10:00');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [commsResult, membersResult] = await Promise.all([
        supabase
          .from('communications')
          .select('*')
          .order('sent_at', { ascending: false })
          .limit(20),
        supabase.from('members').select('*').eq('status', 'Active'),
      ]);

      if (commsResult.data) setCommunications(commsResult.data);
      if (membersResult.data) {
        setMembers(membersResult.data);
        if (membersResult.data.length > 0) {
          setSelectedMember(membersResult.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const templates = [
    {
      id: 1,
      name: 'Appointment Reminder',
      category: 'Appointments',
      content:
        'Hi {{name}}, this is a reminder about your appointment on {{date}} at {{time}}. Please call us if you need to reschedule.',
    },
    {
      id: 2,
      name: 'Medication Adherence',
      category: 'Clinical',
      content:
        'Hello {{name}}, our records show you may be due for a medication refill. Please contact your pharmacy or call us if you need assistance.',
    },
    {
      id: 3,
      name: 'Care Gap Closure',
      category: 'Quality',
      content:
        'Hi {{name}}, we noticed you are due for your annual wellness visit. Staying up to date with preventive care is important for your health. Would you like to schedule?',
    },
    {
      id: 4,
      name: 'Follow-up Check',
      category: 'General',
      content:
        'Hello {{name}}, I wanted to check in and see how you are feeling. Please let me know if you have any questions or concerns.',
    },
    {
      id: 5,
      name: 'Transportation Assistance',
      category: 'Social',
      content:
        'Hi {{name}}, we can help arrange transportation for your medical appointments. Please let us know if you need this service.',
    },
    {
      id: 6,
      name: 'Post-Discharge',
      category: 'Transitions',
      content:
        'Hello {{name}}, we hope you are recovering well. Please reach out if you have any questions about your medications or care plan.',
    },
  ];

  const generateAIDraft = (tone: 'empathetic' | 'concise' | 'formal') => {
    const selectedMemberData = members.find((m) => m.id === selectedMember);
    if (!selectedMemberData) return;

    const name = selectedMemberData.first_name;

    const drafts = {
      empathetic: `Hi ${name}, I hope this message finds you well. I wanted to personally reach out because I care about your health and wellbeing. We noticed you might benefit from a check-in appointment. Your health journey is important to us, and we're here to support you every step of the way. Would you be comfortable scheduling a time to talk? We can work around your schedule. Warm regards, Your Care Team`,
      concise: `Hi ${name}, Time for your health check-in. Can you schedule an appointment this week? Reply YES to confirm or call us. Thanks, Care Team`,
      formal: `Dear ${name}, We are writing to inform you that you are due for a routine health assessment as part of your care plan. Please contact our office at your earliest convenience to schedule an appointment. We appreciate your continued cooperation in maintaining your health. Sincerely, Care Management Team`,
    };

    setMessage(drafts[tone]);
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'SMS':
        return MessageSquare;
      case 'Email':
        return Mail;
      case 'WhatsApp':
        return Smartphone;
      case 'IVR':
      case 'Phone':
        return Phone;
      default:
        return MessageSquare;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading outreach center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full space-x-6">
      {/* Left Sidebar - Conversation History */}
      <div className="w-80 bg-white rounded-lg border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Conversation History</h2>
        </div>
        <div className="flex-1 overflow-y-auto">
          {communications.map((comm) => {
            const Icon = getChannelIcon(comm.channel);
            const member = members.find((m) => m.id === comm.member_id);
            return (
              <div
                key={comm.id}
                className="p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Icon className="w-4 h-4 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {member
                        ? `${member.first_name} ${member.last_name}`
                        : 'Unknown'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDateTime(comm.sent_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 truncate">{comm.message}</p>
                <div className="flex items-center mt-2 space-x-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      comm.direction === 'Outbound'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {comm.direction}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      comm.status === 'Delivered'
                        ? 'bg-green-100 text-green-800'
                        : comm.status === 'Failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {comm.status}
                  </span>
                  {comm.ai_generated && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 flex items-center">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Area */}
      <div className="flex-1 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outreach Center</h1>
          <p className="text-gray-600 mt-1">
            Compose and send personalized member communications
          </p>
        </div>

        {/* Member Selector and Channel */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Member
              </label>
              <select
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.first_name} {member.last_name} ({member.member_id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Channel
              </label>
              <div className="flex space-x-2">
                {(['SMS', 'Email', 'WhatsApp', 'IVR'] as const).map(
                  (channel) => {
                    const icons = {
                      SMS: MessageSquare,
                      Email: Mail,
                      WhatsApp: Smartphone,
                      IVR: PhoneCall,
                    };
                    const Icon = icons[channel];
                    return (
                      <button
                        key={channel}
                        onClick={() => setSelectedChannel(channel)}
                        className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg border-2 transition-colors ${
                          selectedChannel === channel
                            ? 'bg-purple-50 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        <Icon className="w-5 h-5 mr-2" />
                        {channel}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Template Library */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Template Library
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  const member = members.find((m) => m.id === selectedMember);
                  if (member) {
                    const filled = template.content
                      .replace('{{name}}', member.first_name)
                      .replace('{{date}}', 'tomorrow')
                      .replace('{{time}}', '10:00 AM');
                    setMessage(filled);
                  }
                }}
                className="text-left p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {template.name}
                  </span>
                  <span className="text-xs px-2 py-1 bg-white rounded-full text-gray-600 border border-gray-200">
                    {template.category}
                  </span>
                </div>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {template.content}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Message Composer */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Compose Message
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => generateAIDraft('empathetic')}
                className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm border border-purple-200 flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Empathetic
              </button>
              <button
                onClick={() => generateAIDraft('concise')}
                className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm border border-blue-200 flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Concise
              </button>
              <button
                onClick={() => generateAIDraft('formal')}
                className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm border border-purple-200 flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Formal
              </button>
            </div>
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            rows={8}
          />

          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-600">
              {message.length} characters
              {selectedChannel === 'SMS' && message.length > 160 && (
                <span className="text-amber-600 ml-2">
                  (Will be sent as {Math.ceil(message.length / 160)} messages)
                </span>
              )}
            </span>
            <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center">
              <Send className="w-5 h-5 mr-2" />
              Send Message
            </button>
          </div>
        </div>

        {/* Follow-up Scheduler */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Calendar className="w-5 h-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Schedule Follow-up
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time
              </label>
              <input
                type="time"
                value={followUpTime}
                onChange={(e) => setFollowUpTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <button className="mt-4 px-6 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors border border-purple-200 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Schedule Follow-up
          </button>
        </div>
      </div>
    </div>
  );
}
